import { useEffect, useRef, useState } from 'react';
import { useProjectViewStore } from '@/stores/projectView';

interface DashboardNotesSectionProps {
  projectId: number;
  initialNotes: string;
  /** When true the notes are display-only: the textarea is read-only, no autosave
   *  fires (the PATCH requires `project:notes:write`), and the save indicator is
   *  hidden. Used for roles that may read but not edit the dashboard (e.g. oplijster). */
  readOnly?: boolean;
}

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/** Local HH:MM for the "Opgeslagen om HH:MM" indicator. */
function nowHHMM(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Project-wide free-text notes (Fase 4) — plain-text autosave with a 600ms
 * debounce. Conflict policy is last-write-wins (own field, negligible concurrent
 * edits). A pending save is flushed on unmount so a note typed right before a
 * tab/project switch is not dropped (mirrors TaskDetailPanel).
 */
export function DashboardNotesSection({ projectId, initialNotes, readOnly = false }: DashboardNotesSectionProps) {
  const updateNotes = useProjectViewStore((s) => s.updateNotes);
  const [text, setText] = useState(initialNotes);
  const [save, setSave] = useState<SaveState>('idle');
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const timer = useRef<number | null>(null);
  const activeProjectId = useRef(projectId);
  const pending = useRef<{ projectId: number; value: string } | null>(null);

  // Reset the editor ONLY when a different project is opened — keyed on
  // projectId, NOT initialNotes. The optimistic store write round-trips the
  // saved value back into initialNotes; if that re-triggered this effect it
  // would clobber characters typed during the debounce window (and reset the
  // save indicator). Reading initialNotes from the render closure is correct:
  // a project switch re-runs the effect with the new project's value, and a
  // tab-switch remount re-initialises via useState(initialNotes).
  useEffect(() => {
    activeProjectId.current = projectId;
    setText(initialNotes);
    setSave('idle');
    setSavedAt(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset on project switch only; see comment above
  }, [projectId]);

  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
    const p = pending.current;
    if (p) void updateNotes(p.projectId, p.value); // fire-and-forget flush
  }, [updateNotes]);

  const onChange = (value: string) => {
    setText(value);
    setSave('saving');
    if (timer.current) window.clearTimeout(timer.current);
    const id = projectId;
    pending.current = { projectId: id, value };
    timer.current = window.setTimeout(() => {
      pending.current = null;
      void updateNotes(id, value).then((ok) => {
        if (activeProjectId.current !== id) return; // project switched mid-save
        if (ok) {
          setSave('saved');
          setSavedAt(nowHHMM());
        } else {
          setSave('error');
        }
      });
    }, 600);
  };

  return (
    <section
      aria-label="Projectnotities"
      className="rounded-xl border border-gray-200 border-l-4 border-l-td-navy bg-white p-4 shadow-sm"
    >
      <div className="mb-1 flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-td-muted">
          <span aria-hidden="true">📝</span>
          <label htmlFor="dashboard-notes">Notities</label>
        </span>
        <span className="text-[11px]" aria-live="polite">
          {readOnly ? (
            <span className="text-td-muted">Alleen-lezen</span>
          ) : (
            <>
              {save === 'saving' && <span className="text-td-muted">Opslaan…</span>}
              {save === 'saved' && <span className="text-td-green">Opgeslagen{savedAt ? ` om ${savedAt}` : ''}</span>}
              {save === 'error' && <span className="text-td-magenta">Opslaan mislukt</span>}
            </>
          )}
        </span>
      </div>
      <textarea
        id="dashboard-notes"
        value={text}
        onChange={readOnly ? undefined : (e) => onChange(e.target.value)}
        readOnly={readOnly}
        rows={5}
        placeholder={readOnly ? 'Geen notities voor dit project.' : 'Algemene notities voor dit project…'}
        className={`w-full resize-y whitespace-pre-wrap rounded-lg border border-gray-200 p-2.5 text-sm text-td-navy ${
          readOnly
            ? 'bg-gray-50/60 cursor-default'
            : 'bg-gray-50/60 focus-visible:border-td-blue focus-visible:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-td-blue/30'
        }`}
      />
    </section>
  );
}
